/* ===================================================================
   LUMEN SERIES B — FULL SIMULATED LEGAL DOCUMENTS  (extracted from App.jsx)
   Realistic-length seed documents. The inline text here is the source of
   truth that scripts/gen-docs.mjs renders into real .docx files in
   public/documents/. Keep the LUMEN_DOC_FILES map in sync with the
   attachment filenames used in the corpus + workstream seeds.
=================================================================== */
const TERM_SHEET_V1 = `TERM SHEET
LUMEN ROBOTICS, INC.
SERIES B PREFERRED STOCK FINANCING

This Term Sheet (the "Term Sheet") summarizes the principal terms of a
proposed Series B Preferred Stock financing of Lumen Robotics, Inc., a
Delaware corporation (the "Company"). Except for the sections titled
"Exclusivity," "Confidentiality," and "Expenses," this Term Sheet is
non-binding and is subject to the negotiation and execution of definitive
documentation.

Issuer:              Lumen Robotics, Inc.
Securities:          Series B Preferred Stock (the "Series B Preferred")
Amount:              $40,000,000 aggregate purchase price
Lead Investor:       Ridgeline Ventures, LP ("Ridgeline")
Other Investors:     Foundry Capital, Seedling Partners, Northstar Fund
                     (collectively, with Ridgeline, the "Investors")

VALUATION
Pre-Money Valuation: $175,000,000 (on a fully-diluted basis, including
                     an Available Option Pool equal to 11.0% of the
                     post-Closing fully-diluted capitalization)
Post-Money Valuation: $215,000,000
Price per Share:     To be determined at Closing based on the resulting
                     fully-diluted capitalization

1.  DIVIDENDS
The Series B Preferred shall accrue cumulative dividends at an annual
rate of 6% of the Original Issue Price, payable upon a liquidation event,
redemption or conversion. Dividends shall be paid in preference to any
dividend on the Common Stock.

2.  LIQUIDATION PREFERENCE
In the event of any liquidation, dissolution, winding up or Deemed
Liquidation Event of the Company, the holders of Series B Preferred
shall be entitled to receive, prior and in preference to the holders of
Common Stock, an amount equal to one times (1x) the Original Issue Price,
plus all accrued and unpaid dividends (the "Series B Preference"). The
Series B Preference shall be non-participating; following payment of the
Series B Preference, the remaining assets shall be distributed pro rata
among the holders of Common Stock.

A "Deemed Liquidation Event" shall include (a) a merger or consolidation
in which the Company is not the surviving entity, (b) the sale of all
or substantially all of the Company's assets, or (c) any transaction in
which a majority of the Company's voting power is transferred.

3.  CONVERSION
Each share of Series B Preferred shall be convertible, at the option
of the holder, at any time and without payment of additional
consideration, into one (1) share of Common Stock (subject to adjustment
for stock splits, recapitalizations, and the anti-dilution adjustments
described below).

4.  ANTI-DILUTION
The conversion price of the Series B Preferred shall be subject to
broad-based weighted average adjustment in the event of a Down Round
issuance of Additional Shares, calculated in accordance with the
standard NVCA broad-based weighted average formula. Customary carve-outs
shall apply for option pool issuances, M&A consideration, equipment
leasing arrangements, and conversions of existing securities.

5.  VOTING RIGHTS
Each share of Series B Preferred shall vote on an as-converted basis
with the Common Stock on all matters, except as required by law or as
provided in the Protective Provisions.

6.  PROTECTIVE PROVISIONS
For so long as 25% of the Series B Preferred remains outstanding, the
consent of the holders of a majority of the Series B Preferred shall
be required for the Company to: (a) liquidate, dissolve or wind up;
(b) amend its certificate of incorporation or bylaws; (c) create or
authorize any equity security senior to or pari passu with the Series B
Preferred; (d) increase the authorized number of shares of Common Stock
or Preferred Stock; (e) incur indebtedness in excess of $5,000,000;
(f) enter into any transaction with an affiliate; or (g) change the
size of the Board of Directors.

7.  BOARD OF DIRECTORS
Upon Closing, the Board shall consist of five (5) directors:
   (a) two (2) directors designated by the holders of Common Stock,
       initially the Company's Chief Executive Officer and one other
       founder representative;
   (b) one (1) director designated by Ridgeline (the "Series B
       Director"), initially Alex Morgan;
   (c) one (1) director designated by the holders of Series A
       Preferred Stock; and
   (d) one (1) independent director mutually acceptable to the
       Company and Ridgeline.

8.  INFORMATION AND INSPECTION RIGHTS
The Company shall deliver to each Major Investor: (a) audited annual
financial statements within 120 days of fiscal year end; (b) unaudited
quarterly financial statements within 45 days of quarter end; (c) annual
budget at least 30 days prior to fiscal year end; and (d) such other
information as the Major Investor may reasonably request. A "Major
Investor" means a holder of at least 1,000,000 shares of Series B
Preferred (as adjusted).

9.  REGISTRATION RIGHTS
The Company shall grant the Investors customary demand, S-3 and
piggyback registration rights pursuant to an Investors' Rights
Agreement, including two (2) Form S-1 demand registrations and
unlimited Form S-3 demand registrations following the Company's IPO.

10. RIGHT OF FIRST REFUSAL & CO-SALE
The Major Investors shall have a right of first refusal and co-sale
right on transfers of shares by the Founders, subject to customary
carve-outs (estate planning, charitable transfers, and transfers up
to 5% of Founder holdings).

11. PREEMPTIVE RIGHTS
The Major Investors shall have a pro rata preemptive right to
participate in future financings, subject to customary exclusions.

12. EMPLOYEE MATTERS
Each Founder and key employee shall enter into a Confidential Information
and Invention Assignment Agreement, and equity grants shall vest over
four (4) years with a one-year cliff. The Available Option Pool shall
equal 11.0% of the post-Closing fully-diluted capitalization.

13. CONDITIONS TO CLOSING
The obligation of the Investors to purchase the Series B Preferred shall
be subject to: (a) satisfactory completion of legal, financial and
technical due diligence; (b) execution of definitive financing
documentation in form satisfactory to the Investors; (c) delivery of
customary legal opinions, secretary's certificate and good standing
certificates; (d) no material adverse change in the business; and
(e) such other conditions as are customary for a transaction of this
nature.

14. EXCLUSIVITY (Binding)
For a period of forty-five (45) days following execution of this Term
Sheet, the Company and its representatives shall not solicit, encourage
or negotiate any competing financing proposal.

15. CONFIDENTIALITY (Binding)
The existence and terms of this Term Sheet, and all discussions related
thereto, shall be held in confidence by the parties.

16. EXPENSES (Binding)
Upon Closing, the Company shall reimburse Ridgeline for its reasonable
documented legal fees, capped at $75,000.

17. GOVERNING LAW
This Term Sheet and the contemplated financing shall be governed by
Delaware law.

[REMAINDER OF PAGE INTENTIONALLY LEFT BLANK]

For Ridgeline Ventures, LP:                For Lumen Robotics, Inc.:

By: _____________________                 By: _____________________
    Alex Morgan, Partner                       Sarah Chen, CEO
    Date:                                      Date:
`;

const TERM_SHEET_SIGNED = `TERM SHEET
LUMEN ROBOTICS, INC.
SERIES B PREFERRED STOCK FINANCING — EXECUTION COPY

This Term Sheet (the "Term Sheet") summarizes the principal terms of the
Series B Preferred Stock financing of Lumen Robotics, Inc., a Delaware
corporation (the "Company"). Except for the sections titled "Exclusivity,"
"Confidentiality," and "Expenses," this Term Sheet is non-binding and is
subject to the negotiation and execution of definitive documentation.

Issuer:              Lumen Robotics, Inc.
Securities:          Series B Preferred Stock (the "Series B Preferred")
Amount:              $45,000,000 aggregate purchase price
Lead Investor:       Ridgeline Ventures, LP ("Ridgeline")  ($30,000,000)
Other Investors:     Foundry Capital ($10,000,000),
                     Seedling Partners ($3,000,000),
                     Northstar Fund ($2,000,000)

VALUATION
Pre-Money Valuation: $180,000,000 (on a fully-diluted basis, including
                     an Available Option Pool equal to 11.0% of the
                     post-Closing fully-diluted capitalization)
Post-Money Valuation: $225,000,000
Price per Share:     $12.83 (to be confirmed at Closing)

1.  DIVIDENDS
6% cumulative, accruing, payable on a liquidation event, redemption or
conversion, in preference to any dividend on the Common Stock.

2.  LIQUIDATION PREFERENCE
1x non-participating preference, plus accrued and unpaid dividends.
Following payment of the preference, residual assets distributed pro rata
to the Common Stock. Standard Deemed Liquidation Events apply (merger,
asset sale, change of voting control).

3.  CONVERSION
Each share of Series B Preferred convertible into one (1) share of Common
Stock at the option of the holder, subject to standard adjustments.
Mandatory conversion upon (a) a Qualified IPO at a price not less than
3x the Original Issue Price and gross proceeds of at least $75 million,
or (b) the vote of a majority of the Series B Preferred.

4.  ANTI-DILUTION
Broad-based weighted average. Customary carve-outs.

5.  VOTING RIGHTS
As-converted basis with Common, except as provided in the Protective
Provisions or by law.

6.  PROTECTIVE PROVISIONS
For so long as 25% of the Series B Preferred remains outstanding, the
consent of the holders of a majority of the Series B Preferred shall
be required for the actions described in Section 6 of the long-form
Charter, including (a) liquidation or winding up; (b) amendment of the
certificate of incorporation; (c) creation of senior or pari passu
securities; (d) issuance of indebtedness in excess of $5,000,000;
(e) related-party transactions; and (f) changes to the size of the
Board.

7.  BOARD OF DIRECTORS
Five (5) directors: two (2) Common, one (1) Series B (Ridgeline:
Alex Morgan), one (1) Series A, and one (1) independent mutually
acceptable. The independent seat will remain vacant at Closing pending
mutual selection.

8.  INFORMATION RIGHTS, REGISTRATION RIGHTS, ROFR/CO-SALE, PREEMPTIVE
RIGHTS, EMPLOYEE MATTERS
As set forth in the Investors' Rights Agreement, Right of First Refusal
and Co-Sale Agreement, and Voting Agreement, in each case in
substantially the form of the NVCA model documents, with such changes
as the parties may mutually agree.

9.  OPTION POOL
The Available Option Pool shall equal 11.0% of the post-Closing
fully-diluted capitalization. The pre-Closing top-up will be effected
prior to the Closing such that the pre-money fully-diluted
capitalization reflects the full pool.

10. CONDITIONS TO CLOSING
Customary: legal, financial and technical diligence; executed definitive
documentation; legal opinion of Company counsel; secretary's certificate;
good standing; no MAC.

11. EXCLUSIVITY (Binding)
Forty-five (45) days from execution.

12. CONFIDENTIALITY (Binding)
Standard.

13. EXPENSES (Binding)
Reasonable documented legal fees of Ridgeline, capped at $75,000,
payable upon Closing.

14. GOVERNING LAW
Delaware.

EXECUTED THIS 10th DAY OF APRIL, 2026.

For Ridgeline Ventures, LP:

By: /s/ Alex Morgan
    Alex Morgan, Partner
    Date: April 10, 2026

For Lumen Robotics, Inc.:

By: /s/ Sarah Chen
    Sarah Chen, Chief Executive Officer
    Date: April 10, 2026
`;

const SPA_V1 = `STOCK PURCHASE AGREEMENT

This SERIES B PREFERRED STOCK PURCHASE AGREEMENT (this "Agreement") is
made as of April 17, 2026, by and among LUMEN ROBOTICS, INC., a
Delaware corporation (the "Company"), and the investors listed on
Schedule A hereto (each, an "Investor" and collectively, the
"Investors").

RECITALS

WHEREAS, the Company desires to issue and sell to the Investors, and
the Investors desire to purchase from the Company, shares of the
Company's Series B Preferred Stock, par value $0.0001 per share (the
"Series B Preferred"), having the rights, preferences and privileges
set forth in the Amended and Restated Certificate of Incorporation of
the Company (the "Restated Certificate"), in the form attached as
Exhibit A hereto;

NOW, THEREFORE, in consideration of the foregoing and the mutual
covenants set forth herein, the parties agree as follows:

1.  PURCHASE AND SALE OF SERIES B PREFERRED

1.1 Sale and Issuance. Subject to the terms and conditions of this
Agreement, each Investor agrees, severally and not jointly, to purchase
at the Closing, and the Company agrees to sell and issue to each
Investor at the Closing, that number of shares of Series B Preferred
set forth opposite such Investor's name on Schedule A, at a purchase
price of $12.83 per share (the "Original Issue Price").

1.2 Closing. The purchase and sale of the Series B Preferred shall
take place remotely via the exchange of documents and signatures at
such time and place as the Company and the Investors mutually agree
(the "Closing"). At the Closing, the Company shall deliver to each
Investor a certificate or book-entry statement representing the Series B
Preferred being purchased against payment of the purchase price.

1.3 Use of Proceeds. The Company shall use the proceeds from the sale
of the Series B Preferred for general corporate purposes, including
without limitation working capital and product development.

2.  CONSIDERATION AND AMOUNT

2.1 Round Size. The aggregate gross proceeds from the sale of Series B
Preferred shall be $45,000,000 (the "Round Size").

2.2 Pre-Money Valuation. The pre-money valuation of the Company is
$180,000,000, on a fully-diluted basis (including the Available Option
Pool of 11.0% post-Closing).

2.3 Allocation. The Round Size shall be allocated among the Investors
as set forth on Schedule A.

3.  REPRESENTATIONS AND WARRANTIES OF THE COMPANY

The Company hereby represents and warrants to each Investor that, except
as set forth in the Disclosure Schedule, the following are true and
correct as of the Closing:

3.1 Organization. The Company is a corporation duly organized,
validly existing and in good standing under the laws of the State of
Delaware, with full corporate power and authority to own, operate, and
lease its properties and to carry on its business as now conducted.

3.2 Capitalization. The authorized capital stock of the Company, as of
immediately prior to the Closing, consists of the shares set forth on
the capitalization table attached as Exhibit B (the "Capitalization
Table"). The Capitalization Table sets forth a complete and accurate
list of all issued and outstanding shares of the Company's capital
stock and all options, warrants, and other rights to acquire capital
stock of the Company. All issued and outstanding shares are duly
authorized, validly issued, fully paid, non-assessable, and free of
preemptive rights, except as set forth in the Restated Certificate
and the agreements identified in the Disclosure Schedule.

3.3 Authorization. All corporate action on the part of the Company
necessary for the authorization, execution, delivery and performance
of this Agreement and the other Transaction Documents and the
authorization, sale, issuance and delivery of the Series B Preferred
has been taken or will be taken prior to the Closing.

3.4 Valid Issuance. The Series B Preferred, when issued and paid for
as provided in this Agreement, will be duly authorized, validly issued,
fully paid and non-assessable.

3.5 Litigation. There is no action, suit, proceeding or investigation
pending or, to the Company's knowledge, currently threatened against
the Company that questions the validity of this Agreement or the right
of the Company to enter into it.

3.6 Intellectual Property. The Company owns or possesses sufficient
legal rights to all Intellectual Property used in the conduct of its
business as now conducted without any infringement of the rights of
others.

3.7 Compliance with Other Instruments. The Company is not in violation
of any provision of its Restated Certificate or Bylaws, or, in any
material respect, of any instrument, judgment, order or agreement to
which it is a party or by which it is bound.

3.8 Material Contracts. Section 3.8 of the Disclosure Schedule lists
each Material Contract, including the Master Services Agreement with
Acme Health Systems (the "Acme MSA"), which is currently being
renegotiated in the ordinary course.

3.9 Financial Statements. The Company has delivered to each Investor
its audited financial statements for the fiscal years ended December
31, 2024 and December 31, 2025, and its unaudited financial statements
for the quarter ended March 31, 2026 (collectively, the "Financial
Statements"). The Financial Statements have been prepared in
accordance with GAAP applied on a consistent basis.

3.10 No Material Adverse Change. Since the date of the most recent
Financial Statements, there has not been any material adverse change
in the assets, liabilities, financial condition, operating results, or
business of the Company.

4.  REPRESENTATIONS AND WARRANTIES OF THE INVESTORS

Each Investor hereby represents and warrants to the Company, severally
and not jointly, as follows: (a) such Investor is an "accredited
investor" within the meaning of Rule 501 of Regulation D;
(b) such Investor has full power and authority to enter into this
Agreement; (c) the Series B Preferred is being acquired for such
Investor's own account, for investment purposes only; and (d) such
Investor understands that the Series B Preferred has not been registered
under the Securities Act and may not be sold absent registration or
an exemption therefrom.

5.  CONDITIONS TO INVESTORS' OBLIGATIONS AT CLOSING

5.1 Representations and Warranties. The representations and warranties
of the Company contained in Section 3 shall be true and correct in all
material respects on and as of the Closing.

5.2 Performance. The Company shall have performed and complied with
all covenants, agreements, obligations, and conditions contained in
this Agreement that are required to be performed or complied with by
it on or before the Closing.

5.3 Filing of Restated Certificate. The Restated Certificate shall
have been duly filed with the Secretary of State of the State of
Delaware.

5.4 Ancillary Agreements. Each of the Investors' Rights Agreement,
the Right of First Refusal and Co-Sale Agreement, and the Voting
Agreement shall have been executed and delivered by all parties
thereto.

5.5 Legal Opinion. The Investors shall have received from Carter
Whitman LLP, counsel to the Company, a customary legal opinion in
form and substance reasonably satisfactory to the Investors.

5.6 Secretary's Certificate. The Company shall have delivered a
certificate, executed by its Secretary, certifying (a) the Restated
Certificate and Bylaws, (b) the resolutions adopted by the Board
authorizing the Transaction, and (c) the good standing of the Company.

5.7 No Material Adverse Effect. There shall have been no Material
Adverse Effect since the date of this Agreement.

6.  MISCELLANEOUS

6.1 Survival of Warranties. The representations and warranties of the
Company contained in Section 3 shall survive the Closing for a period
of two (2) years.

6.2 Successors and Assigns. The terms and conditions of this Agreement
shall inure to the benefit of and be binding upon the respective
successors and assigns of the parties.

6.3 Governing Law. This Agreement shall be governed by and construed
in accordance with the laws of the State of Delaware, without regard
to its conflict of laws provisions.

6.4 Counterparts; Electronic Signature. This Agreement may be executed
in counterparts (including by .pdf or DocuSign), each of which shall be
deemed an original but all of which together shall constitute one and
the same instrument.

6.5 Notices. All notices required or permitted hereunder shall be in
writing and shall be deemed effectively given upon delivery.

6.6 Entire Agreement. This Agreement (together with the other
Transaction Documents) constitutes the full and entire understanding
and agreement among the parties with respect to the subject matter
hereof.

[SIGNATURE PAGES FOLLOW]

COMPANY:                          INVESTORS:

LUMEN ROBOTICS, INC.              RIDGELINE VENTURES, LP

By: _______________________       By: _______________________
    Sarah Chen, CEO                   Alex Morgan, Partner

                                  FOUNDRY CAPITAL
                                  By: _______________________

                                  SEEDLING PARTNERS
                                  By: _______________________

                                  NORTHSTAR FUND
                                  By: _______________________

SCHEDULE A — INVESTORS AND PURCHASE COMMITMENTS

Ridgeline Ventures, LP        2,338,272 shares     $30,000,000
Foundry Capital                 779,424 shares     $10,000,000
Seedling Partners               233,827 shares      $3,000,000
Northstar Fund                  155,885 shares      $2,000,000
TOTAL                         3,507,408 shares     $45,000,000

EXHIBIT A — Form of Restated Certificate of Incorporation
EXHIBIT B — Capitalization Table (Pro Forma)
EXHIBIT C — Disclosure Schedule
EXHIBIT D — Form of Investors' Rights Agreement
EXHIBIT E — Form of ROFR & Co-Sale Agreement
EXHIBIT F — Form of Voting Agreement
`;

const CHARTER_V1 = `AMENDED AND RESTATED
CERTIFICATE OF INCORPORATION
OF
LUMEN ROBOTICS, INC.

(Pursuant to Sections 242 and 245 of the
General Corporation Law of the State of Delaware)

Lumen Robotics, Inc., a corporation organized and existing under the
General Corporation Law of the State of Delaware (the "Corporation"),
hereby certifies that:

   FIRST: The name of the Corporation is Lumen Robotics, Inc.

   SECOND: The original Certificate of Incorporation was filed with the
Secretary of State of the State of Delaware on June 14, 2022.

   THIRD: The Certificate of Incorporation of the Corporation is hereby
amended and restated to read in its entirety as follows:

ARTICLE I — NAME
The name of the Corporation is Lumen Robotics, Inc.

ARTICLE II — REGISTERED OFFICE
The address of the Corporation's registered office in the State of
Delaware is 251 Little Falls Drive, in the City of Wilmington, County of
New Castle, 19808. The name of its registered agent at such address is
Corporation Service Company.

ARTICLE III — PURPOSE
The purpose of the Corporation is to engage in any lawful act or activity
for which corporations may be organized under the Delaware General
Corporation Law.

ARTICLE IV — CAPITAL STOCK

Section 1. Authorized Shares.
The Corporation is authorized to issue two classes of stock to be
designated, respectively, "Common Stock" and "Preferred Stock." The
total number of shares which the Corporation is authorized to issue is
40,000,000 shares: 30,000,000 shares of Common Stock, $0.0001 par value
per share, and 10,000,000 shares of Preferred Stock, $0.0001 par value
per share.

Of the Preferred Stock, 2,750,000 shares are hereby designated "Series
Seed Preferred Stock"; 2,950,000 shares are hereby designated "Series A
Preferred Stock"; and 3,507,408 shares are hereby designated "Series B
Preferred Stock."

Section 2. Liquidation.

   (a) Liquidation Preference. In the event of any liquidation,
   dissolution or winding up of the Corporation, whether voluntary or
   involuntary, or any Deemed Liquidation Event (as defined below), the
   holders of Series B Preferred Stock shall be entitled to receive,
   prior and in preference to any distribution of the assets of the
   Corporation to the holders of Series A Preferred Stock, Series Seed
   Preferred Stock or Common Stock, an amount per share equal to the
   sum of (i) one times (1x) the Original Issue Price of the Series B
   Preferred Stock, plus (ii) all accrued but unpaid dividends thereon
   (the "Series B Preference"). After payment of the Series B
   Preference, the holders of Series B Preferred Stock shall be entitled
   to participate, on an as-converted basis with the holders of Common
   Stock, in the distribution of the remaining assets of the Corporation
   until the holders of Series B Preferred Stock have received an
   aggregate amount equal to two times (2x) the Original Issue Price
   (the "Participation Cap").

   [DRAFTING NOTE — preference type carried over from prior template;
   confirm against the executed Term Sheet during the first comment
   round.]

   (b) Series A Preference. After payment of the Series B Preference,
   the holders of Series A Preferred Stock shall be entitled to receive
   an amount equal to 1x the Series A Original Issue Price.

   (c) Series Seed Preference. After payment of the Series A Preference,
   the holders of Series Seed Preferred Stock shall be entitled to
   receive an amount equal to 1x the Series Seed Original Issue Price.

   (d) Remaining Distribution. Any remaining assets shall be distributed
   pro rata to the holders of Common Stock.

   (e) Deemed Liquidation Event. A "Deemed Liquidation Event" means
   (i) a merger or consolidation in which the Corporation is not the
   surviving entity (other than a mere reincorporation); (ii) the sale,
   lease, exclusive license or other disposition of all or substantially
   all of the assets of the Corporation; or (iii) any transaction or
   series of related transactions in which a majority of the
   Corporation's outstanding voting power is transferred.

Section 3. Voting.
Each holder of outstanding shares of Preferred Stock shall be entitled
to cast the number of votes equal to the number of whole shares of
Common Stock into which such shares of Preferred Stock are convertible
immediately after the close of business on the record date.

Section 4. Conversion.
Each share of Preferred Stock shall be convertible, at the option of
the holder, into such number of fully-paid and non-assessable shares of
Common Stock as is determined by dividing the Original Issue Price by
the Conversion Price applicable to such share, in effect at the time of
conversion. The initial Conversion Price for each series of Preferred
Stock shall be equal to its Original Issue Price.

Section 5. Anti-Dilution Adjustments.
The Conversion Price of each series of Preferred Stock shall be subject
to adjustment from time to time on a broad-based weighted average basis
in the event that the Corporation issues Additional Shares of Common
Stock for a consideration per share less than the Conversion Price in
effect immediately prior to such issuance.

Section 6. Protective Provisions.
For so long as not less than 25% of the originally issued shares of
Series B Preferred Stock remain outstanding, the Corporation shall not,
without first obtaining the written consent of the holders of a majority
of the then outstanding shares of Series B Preferred Stock:

   (a) liquidate, dissolve or wind up the Corporation, or effect any
   Deemed Liquidation Event;
   (b) amend, alter, or repeal any provision of this Certificate or the
   Bylaws of the Corporation;
   (c) create or authorize the issuance of any new class or series of
   equity securities senior to or pari passu with the Series B
   Preferred Stock;
   (d) increase or decrease the authorized number of shares of any
   class or series of stock;
   (e) incur indebtedness in excess of $5,000,000 in the aggregate;
   (f) enter into any transaction with an affiliate; or
   (g) change the size of the Board of Directors.

ARTICLE V — BOARD OF DIRECTORS
The number of directors which constitute the whole Board shall initially
be five (5), composed as follows: (a) two (2) directors designated by
the holders of Common Stock; (b) one (1) director designated by the
holders of Series B Preferred Stock; (c) one (1) director designated by
the holders of Series A Preferred Stock; and (d) one (1) independent
director mutually acceptable to the Common and Series B Preferred.

ARTICLE VI — INDEMNIFICATION
The Corporation shall indemnify its directors and officers to the
fullest extent permitted by the Delaware General Corporation Law.

ARTICLE VII — AMENDMENT
This Certificate may be amended only in accordance with the General
Corporation Law of the State of Delaware and the protective provisions
set forth herein.

IN WITNESS WHEREOF, this Amended and Restated Certificate of
Incorporation has been executed by a duly authorized officer of the
Corporation on this _____ day of _______________, 2026.

LUMEN ROBOTICS, INC.

By: _______________________
    Sarah Chen, CEO
`;

const IRA_V1 = `INVESTORS' RIGHTS AGREEMENT

THIS INVESTORS' RIGHTS AGREEMENT (this "Agreement") is made as of
April 17, 2026, by and among LUMEN ROBOTICS, INC., a Delaware
corporation (the "Company"), and the persons listed on Schedule A
hereto (each, an "Investor" and, collectively, the "Investors").

WHEREAS, the Company proposes to sell and issue Series B Preferred
Stock pursuant to that certain Series B Preferred Stock Purchase
Agreement, dated as of the date hereof (the "Purchase Agreement");

WHEREAS, the Investors are entering into the Purchase Agreement and
desire to receive certain registration rights, information rights, and
other rights as set forth herein;

NOW, THEREFORE, the parties hereby agree as follows:

1.  DEFINITIONS

"Major Investor" means any Investor that, together with its affiliates,
holds at least 1,000,000 shares of Registrable Securities (as adjusted
for stock splits, combinations and similar events).

"Registrable Securities" means (a) the Common Stock issuable or issued
upon conversion of the Preferred Stock, and (b) any other Common Stock
issued as a dividend, distribution, stock split or other recapitalization
with respect to the shares described in clause (a).

"Holder" means any holder of Registrable Securities who is a party to
this Agreement.

2.  REGISTRATION RIGHTS

2.1 Demand Registration. At any time after the earlier of (a) the fifth
anniversary of the date hereof and (b) 180 days after the effective date
of the Company's IPO, the Holders of at least a majority of the
Registrable Securities then outstanding may request that the Company
file a registration statement on Form S-1 covering not less than 30% of
the Registrable Securities. The Company shall be obligated to effect
not more than two such demand registrations.

2.2 Piggyback Registration. If the Company proposes to register any of
its securities for its own account or for the account of any other
holder, the Company shall give written notice to each Holder of its
intention to do so, and each Holder shall have the right to include
their Registrable Securities in such registration, subject to customary
underwriter cutbacks.

2.3 S-3 Registration. Following the Company's IPO, the Holders of at
least 20% of the Registrable Securities then outstanding may request
that the Company file a registration statement on Form S-3 covering
Registrable Securities having an anticipated aggregate offering price
of at least $5,000,000.

2.4 Expenses. All Registration Expenses incurred in connection with any
registration shall be borne by the Company.

3.  INFORMATION AND INSPECTION RIGHTS

3.1 Information Rights. So long as a Major Investor holds at least
500,000 shares of Registrable Securities, the Company shall deliver to
such Major Investor:

   (a) within one hundred twenty (120) days after the end of each
   fiscal year, audited financial statements;
   (b) within forty-five (45) days after the end of each fiscal
   quarter, unaudited financial statements;
   (c) within thirty (30) days after the end of each calendar month,
   monthly management reports;
   (d) at least thirty (30) days prior to the start of each fiscal
   year, an annual operating plan and budget; and
   (e) such other information as such Major Investor may reasonably
   request.

3.2 Inspection Rights. The Company shall permit each Major Investor,
at such Investor's expense, to visit and inspect the Company's
properties, examine its books of account and records and discuss the
Company's affairs with its officers, during normal business hours, upon
reasonable notice.

3.3 Confidentiality. Each Holder agrees that information furnished
pursuant to this Section 3 is confidential and shall not be disclosed
or used except in connection with the evaluation of the Holder's
investment.

4.  RIGHT OF FIRST OFFER (PREEMPTIVE RIGHT)

Subject to the terms and conditions specified in this Section 4, the
Company hereby grants to each Major Investor a right of first offer
with respect to future sales by the Company of New Securities. Each
Major Investor shall have the right to purchase its Pro Rata Portion
of any New Securities that the Company may, from time to time, propose
to sell and issue after the date of this Agreement, on the same terms
and conditions as such New Securities are offered to others.

5.  ADDITIONAL COVENANTS OF THE COMPANY

5.1 Insurance. The Company shall maintain customary directors' and
officers' liability insurance.

5.2 Employee Matters. The Company shall require each employee and
consultant to enter into a Confidential Information and Invention
Assignment Agreement substantially in the form approved by the Board.
Equity grants to employees shall vest over four (4) years with a one
year cliff, subject to acceleration upon a Change in Control followed
by termination without Cause within twelve (12) months thereafter.

5.3 Board Observer. So long as Ridgeline Ventures, LP holds at least
500,000 shares of Registrable Securities, the Company shall permit
Ridgeline to designate one (1) non-voting Board observer.

5.4 Compliance with Laws. The Company shall comply in all material
respects with all applicable laws, rules and regulations.

6.  MISCELLANEOUS

6.1 Termination. The rights and obligations under Sections 3, 4 and 5
shall terminate upon the earlier of (a) the closing of a Qualified IPO,
and (b) a Deemed Liquidation Event.

6.2 Governing Law. This Agreement shall be governed by Delaware law.

6.3 Counterparts. This Agreement may be executed in counterparts.

6.4 Entire Agreement. This Agreement, together with the other
Transaction Documents, constitutes the full and entire understanding
and agreement among the parties.

[SIGNATURE PAGES FOLLOW]

LUMEN ROBOTICS, INC.

By: _______________________      Sarah Chen, CEO

RIDGELINE VENTURES, LP            FOUNDRY CAPITAL
By: _______________________       By: _______________________
    Alex Morgan, Partner

SEEDLING PARTNERS                 NORTHSTAR FUND
By: _______________________       By: _______________________

SCHEDULE A — INVESTORS
Ridgeline Ventures, LP
Foundry Capital
Seedling Partners
Northstar Fund
`;

const ROFR_V1 = `RIGHT OF FIRST REFUSAL AND CO-SALE AGREEMENT

THIS RIGHT OF FIRST REFUSAL AND CO-SALE AGREEMENT (this "Agreement") is
made as of April 17, 2026, by and among LUMEN ROBOTICS, INC. (the
"Company"), the holders of Common Stock listed on Schedule A (each, a
"Key Holder"), and the investors listed on Schedule B (each, an
"Investor").

1.  RIGHT OF FIRST REFUSAL

1.1 Notice of Proposed Transfer. Before any Key Holder may sell, assign,
transfer or otherwise dispose of any Common Stock, such Key Holder
shall deliver to the Company and each Investor a written notice of the
proposed transfer (the "Transfer Notice"), specifying the terms and
conditions, the proposed transferee, and the number of shares.

1.2 Company Right. The Company shall have the right, exercisable within
fifteen (15) days, to purchase all or any portion of the offered shares
on the same terms and conditions.

1.3 Investor Right. To the extent the Company does not exercise its
right in full, each Investor shall have the right to purchase its Pro
Rata Portion of the unsubscribed shares.

2.  CO-SALE RIGHT

If, after the Company and the Investors have failed to exercise the
right of first refusal in full, the Key Holder proposes to consummate
the proposed transfer, each Investor shall have the right to participate
in such transfer on the same terms and conditions, on a pro rata basis.

3.  PERMITTED TRANSFERS

The provisions of Sections 1 and 2 shall not apply to: (a) transfers by
will or intestacy; (b) transfers to family members or to trusts for the
benefit of family members; (c) charitable transfers not to exceed 5% of
the Key Holder's holdings; or (d) transfers approved by the Board.

4.  PROHIBITED TRANSFERS

If a Key Holder makes a Prohibited Transfer, each Investor shall have
the right to require the Key Holder to repurchase such shares, in
addition to any other rights or remedies available.

5.  TERMINATION

This Agreement shall terminate upon (a) the closing of a Qualified IPO,
(b) a Deemed Liquidation Event, or (c) the written consent of the
holders of at least a majority of the then-outstanding Registrable
Securities.

6.  GOVERNING LAW

This Agreement shall be governed by Delaware law.

[Signatures follow]
`;

const VOTING_V1 = `VOTING AGREEMENT

THIS VOTING AGREEMENT (this "Agreement") is made as of April 17, 2026,
by and among LUMEN ROBOTICS, INC. (the "Company"), the holders of
Common Stock listed on Schedule A (each, a "Key Holder"), and the
holders of Preferred Stock listed on Schedule B (each, an "Investor").

1.  BOARD COMPOSITION

The parties shall vote all of their Shares so as to elect a Board of
Directors consisting of five (5) members as follows:

   (a) two (2) Common Directors, designated by the holders of a majority
   of the Common Stock, initially Sarah Chen (CEO) and David Okafor (CFO);

   (b) one (1) Series B Director, designated by the holders of a
   majority of the Series B Preferred Stock, initially Alex Morgan
   (Ridgeline);

   (c) one (1) Series A Director, designated by the holders of a
   majority of the Series A Preferred Stock; and

   (d) one (1) Independent Director, mutually acceptable to the Common
   and the Series B Preferred. The Independent Director seat shall
   remain vacant at Closing pending the parties' mutual selection.

2.  REMOVAL AND REPLACEMENT

Any director designated under Section 1 may be removed only by the
party or group entitled to designate such director, and any vacancy
shall be filled by such party or group.

3.  DRAG-ALONG

If (a) a majority of the Investors and (b) a majority of the Common
Stock approve a Sale of the Company, each party agrees to vote in favor
of, and take all reasonable action to facilitate, such Sale.

4.  TERMINATION

This Agreement shall terminate upon (a) the closing of a Qualified
IPO, (b) a Deemed Liquidation Event, or (c) the written consent of all
parties.

5.  GOVERNING LAW

Delaware.

[Signatures follow]
`;

const CAP_TABLE_V1 = `LUMEN ROBOTICS, INC.
PRE-MONEY CAPITALIZATION TABLE — SERIES B EXHIBIT
As of May 4, 2026

(Prepared by Office of the CFO. For SPA Exhibit B.)

ASSUMPTIONS
   Round Size:              $45,000,000
   Pre-Money Valuation:     $185,000,000          [FLAG — see note below]
   Post-Money Valuation:    $230,000,000
   Price per Share:         $12.47
   Available Option Pool:   11.0% post-Closing

[INTERNAL NOTE — David: We're showing $185M pre on the model because
the option-pool top-up math the team built nets out to that. Legal team
to confirm against the signed Term Sheet (which says $180M). If we keep
$180M, PPS drops to $12.83.]

SECURITY                  HOLDER                   SHARES        % FD
-------------------------------------------------------------------------
Common Stock              Sarah Chen (Founder)     3,800,000     21.40%
Common Stock              David Okafor (Founder)   2,500,000     14.08%
Common Stock              Founders Trust             400,000      2.25%
Series Seed Preferred     Seed Investors           2,700,000     15.21%
Series A Preferred        Series A Investors       2,800,000     15.77%
Options — Outstanding     Employees                1,750,000      9.86%
Options — Available Pool  (To be issued)           1,300,000      7.32% [pre-Closing top-up]
-------------------------------------------------------------------------
PRE-CLOSING TOTAL                                 15,250,000     85.89%

Series B Preferred        Ridgeline Ventures        2,406,576    13.55%
Series B Preferred        Foundry Capital             802,192     4.52%
Series B Preferred        Seedling Partners           240,658     1.36%
Series B Preferred        Northstar Fund              160,438     0.90%
-------------------------------------------------------------------------
POST-CLOSING TOTAL                                18,859,864    100.00%

PRO FORMA CHECK
   Total invested:       $45,000,005.07           (round to $45,000,000)
   Implied PPS:          $12.47

NOTES
   1. Founders' shares vest over 4 years with a 1-year cliff.
   2. Acme Health MSA — flagged as material contract on the
      Disclosure Schedule; in renegotiation.
   3. Pre-money on this schedule reflects the model's option-pool
      treatment; please reconcile against signed TS.
   4. Independent board seat remains TBD.

Prepared by: David Okafor, CFO
Reviewed by: ___________________
Date: May 4, 2026
`;

const CHARTER_V2_REDLINE = `AMENDED AND RESTATED
CERTIFICATE OF INCORPORATION
OF
LUMEN ROBOTICS, INC.

REDLINE — Version 2 vs Version 1
Prepared by: Bradford Yale LLP (Investor Counsel)
Date: April 22, 2026

(Only modified sections shown. All other provisions remain as in v1.)

ARTICLE IV, Section 2 — Liquidation

OLD (v1):
   (a) Liquidation Preference. In the event of any liquidation,
   dissolution or winding up of the Corporation, whether voluntary or
   involuntary, or any Deemed Liquidation Event, the holders of Series B
   Preferred Stock shall be entitled to receive, prior and in preference
   to any distribution of the assets of the Corporation to the holders
   of Series A Preferred Stock, Series Seed Preferred Stock or Common
   Stock, an amount per share equal to the sum of (i) one times (1x)
   the Original Issue Price of the Series B Preferred Stock, plus
   (ii) all accrued but unpaid dividends thereon (the "Series B
   Preference"). After payment of the Series B Preference, the holders
   of Series B Preferred Stock SHALL BE ENTITLED TO PARTICIPATE, ON AN
   AS-CONVERTED BASIS WITH THE HOLDERS OF COMMON STOCK, IN THE
   DISTRIBUTION OF THE REMAINING ASSETS OF THE CORPORATION UNTIL THE
   HOLDERS OF SERIES B PREFERRED STOCK HAVE RECEIVED AN AGGREGATE
   AMOUNT EQUAL TO TWO TIMES (2X) THE ORIGINAL ISSUE PRICE (THE
   "PARTICIPATION CAP").

NEW (v2 — conformed to executed Term Sheet):
   (a) Liquidation Preference. In the event of any liquidation,
   dissolution or winding up of the Corporation, whether voluntary or
   involuntary, or any Deemed Liquidation Event, the holders of Series B
   Preferred Stock shall be entitled to receive, prior and in preference
   to any distribution of the assets of the Corporation to the holders
   of Series A Preferred Stock, Series Seed Preferred Stock or Common
   Stock, an amount per share equal to the sum of (i) one times (1x)
   the Original Issue Price of the Series B Preferred Stock, plus
   (ii) all accrued but unpaid dividends thereon (the "Series B
   Preference"). The Series B Preference shall be NON-PARTICIPATING.
   Following payment of the Series B Preference and any preferences
   payable to the Series A Preferred Stock and Series Seed Preferred
   Stock, the remaining assets of the Corporation shall be distributed
   pro rata to the holders of Common Stock.

EXPLANATION
   The v1 charter inadvertently carried over the "participating with 2x
   cap" preference from a prior template. The signed Term Sheet (April
   10, 2026) is explicit that the Series B Preference is 1x
   non-participating. This redline conforms the Charter to the executed
   Term Sheet.

ALL OTHER PROVISIONS — UNCHANGED.

Reviewed: Elena Vasquez (Bradford Yale)
Approved for issue: Priya Nair (Carter Whitman)
`;

const CHARTER_V3_CLEAN = `AMENDED AND RESTATED
CERTIFICATE OF INCORPORATION
OF
LUMEN ROBOTICS, INC.

(Pursuant to Sections 242 and 245 of the
General Corporation Law of the State of Delaware)

CONFORMED CLEAN — VERSION 3
Filed with the Delaware Secretary of State: May 22, 2026
Effective: May 22, 2026

Lumen Robotics, Inc., a corporation organized and existing under the
General Corporation Law of the State of Delaware (the "Corporation"),
hereby certifies that:

   FIRST: The name of the Corporation is Lumen Robotics, Inc.
   SECOND: The original Certificate of Incorporation was filed with the
Secretary of State of the State of Delaware on June 14, 2022.
   THIRD: The Certificate of Incorporation of the Corporation is hereby
amended and restated to read in its entirety as follows:

ARTICLE I — NAME
The name of the Corporation is Lumen Robotics, Inc.

ARTICLE II — REGISTERED OFFICE
The address of the Corporation's registered office in the State of
Delaware is 251 Little Falls Drive, Wilmington, New Castle County, 19808.
Registered agent: Corporation Service Company.

ARTICLE III — PURPOSE
The purpose of the Corporation is to engage in any lawful act or activity
for which corporations may be organized under the Delaware General
Corporation Law.

ARTICLE IV — CAPITAL STOCK

Section 1. Authorized Shares.
The total number of shares which the Corporation is authorized to issue
is 40,000,000 shares: 30,000,000 shares of Common Stock, $0.0001 par
value per share, and 10,000,000 shares of Preferred Stock, $0.0001 par
value per share. Of the Preferred Stock: 2,750,000 are designated
Series Seed Preferred Stock; 2,950,000 are designated Series A
Preferred Stock; and 3,507,408 are designated Series B Preferred Stock.

Section 2. Liquidation.

   (a) Liquidation Preference. In the event of any liquidation,
   dissolution or winding up of the Corporation, whether voluntary or
   involuntary, or any Deemed Liquidation Event, the holders of Series B
   Preferred Stock shall be entitled to receive, prior and in preference
   to any distribution of the assets of the Corporation to the holders
   of Series A Preferred Stock, Series Seed Preferred Stock or Common
   Stock, an amount per share equal to the sum of (i) one times (1x)
   the Original Issue Price of the Series B Preferred Stock, plus
   (ii) all accrued but unpaid dividends thereon (the "Series B
   Preference"). The Series B Preference shall be non-participating.
   Following payment of the Series B Preference and any preferences
   payable to the Series A Preferred Stock and Series Seed Preferred
   Stock, the remaining assets of the Corporation shall be distributed
   pro rata to the holders of Common Stock.

   (b) Series A Preference. After payment of the Series B Preference,
   the holders of Series A Preferred Stock shall be entitled to receive
   an amount equal to 1x the Series A Original Issue Price.

   (c) Series Seed Preference. After payment of the Series A
   Preference, the holders of Series Seed Preferred Stock shall be
   entitled to receive an amount equal to 1x the Series Seed Original
   Issue Price.

   (d) Remaining Distribution. Any remaining assets shall be distributed
   pro rata to the holders of Common Stock.

   (e) Deemed Liquidation Event. Standard definition (merger, asset
   sale, or change-of-voting-control).

Section 3. Voting.
Each Preferred Stock holder votes on an as-converted basis with Common
Stock, except as provided in Section 6 or by law.

Section 4. Conversion.
Each share of Preferred Stock convertible at the option of the holder
into Common Stock at the Conversion Price; mandatory conversion upon
a Qualified IPO (3x OIP, $75M minimum gross) or majority Series B vote.

Section 5. Anti-Dilution.
Broad-based weighted average. Customary carve-outs apply.

Section 6. Protective Provisions.
For so long as not less than 25% of the originally issued shares of
Series B Preferred Stock remain outstanding, the Corporation shall not,
without first obtaining the written consent of the holders of a majority
of the then outstanding shares of Series B Preferred Stock, take any of
the actions listed in subsections (a)-(g) above.

ARTICLE V — BOARD OF DIRECTORS
Five (5) directors: two (2) Common, one (1) Series B (Ridgeline:
Alex Morgan), one (1) Series A, and one (1) Independent.

ARTICLE VI — INDEMNIFICATION
Maximum indemnification permitted by the Delaware General Corporation
Law.

ARTICLE VII — AMENDMENT
Subject to the protective provisions and the Delaware General
Corporation Law.

IN WITNESS WHEREOF, this Amended and Restated Certificate of
Incorporation has been executed by a duly authorized officer of the
Corporation on this 22nd day of May, 2026.

LUMEN ROBOTICS, INC.

By: /s/ Sarah Chen
    Sarah Chen, CEO

ACCEPTED AND FILED: Delaware Secretary of State
File Number: 7204188-2026
`;

const EXECUTED_PACKAGE = `EXECUTED DOCUMENT PACKAGE — LUMEN ROBOTICS, INC.
SERIES B PREFERRED STOCK FINANCING
Signing Date: May 12, 2026

This package is the final, signed set of definitive agreements for the
Lumen Robotics Series B financing. All signature pages have been
released and the documents are dated as of the Signing Date.

INCLUDED DOCUMENTS

1. SERIES B PREFERRED STOCK PURCHASE AGREEMENT (executed)
   Parties:    Lumen Robotics, Inc.; Ridgeline Ventures, LP; Foundry
               Capital; Seedling Partners; Northstar Fund.
   Headline:   $45,000,000 / $180,000,000 pre-money / $12.83 PPS /
               3,507,408 Series B shares.
   Signatures: Sarah Chen (CEO), Alex Morgan (Ridgeline), and the
               authorized signatories of each Investor.

2. AMENDED AND RESTATED CERTIFICATE OF INCORPORATION (Conformed v3)
   To be filed with the Delaware Secretary of State concurrently with
   Closing. Reflects 1x non-participating Series B preference, broad-
   based weighted-average anti-dilution, and the protective provisions
   in Section 6.

3. INVESTORS' RIGHTS AGREEMENT (executed)
   Registration rights (S-1, S-3, piggyback), information & inspection
   rights, ROFO/preemptive, board observer (Ridgeline), employee
   matters covenants.

4. RIGHT OF FIRST REFUSAL AND CO-SALE AGREEMENT (executed)
   Standard NVCA form. Key Holders: Sarah Chen, David Okafor, Founders
   Trust.

5. VOTING AGREEMENT (executed)
   Board composition: two Common, one Series B (Alex Morgan), one
   Series A, one Independent (TBD). Drag-along on majority Investors +
   majority Common.

6. CLOSING CHECKLIST (Conformed)
   Open conditions to the May 22, 2026 Closing:
   (a) Filing and acceptance of the Restated Certificate by the
       Delaware Secretary of State [OPEN — to occur 5/22 AM]
   (b) Receipt of legal opinion from Carter Whitman LLP [OPEN]
   (c) Secretary's Certificate [OPEN]
   (d) Good standing certificates (Delaware) [OPEN]
   (e) Acme Health MSA renewal status — disclosed as in-negotiation
       material contract [SATISFIED — disclosure schedule updated]
   (f) Wire instructions confirmation from each Investor [OPEN]
   (g) No MAC bring-down by Company [OPEN — at Closing]

7. DISCLOSURE SCHEDULE (final)
   Includes Acme MSA renewal as material contract in negotiation; no
   other material disclosures from the rep drafts.

8. SECRETARY'S CERTIFICATE (to be delivered at Closing)
   Certifying (i) the Restated Certificate, (ii) the Bylaws, (iii) the
   board and stockholder resolutions, and (iv) the incumbency of
   officers.

9. CARTER WHITMAN LLP OPINION (form attached, to be released at
   Closing)

10. FORM W-9 / W-8 PACKAGES (collected from each Investor)

CLOSING TIMETABLE
   May 22, 2026, 8:00 AM ET — Restated Certificate filing posted.
   May 22, 2026, 8:30 AM ET — File acceptance confirmation.
   May 22, 2026, 9:00 AM ET — Wires released by Investors.
   May 22, 2026, 12:00 PM ET — Closing complete.

ALL PARTIES ARE REMINDED that the documents above are governed by
Delaware law and that, until the Restated Certificate is on file and
the funds are received, the Closing remains conditional.

Priya Nair                       Michael Reyes
Senior Associate                 Partner
Carter Whitman LLP               Carter Whitman LLP
`;

const LUMEN_DOCS = {
  TERM_SHEET_V1, TERM_SHEET_SIGNED,
  SPA_V1, CHARTER_V1, IRA_V1, ROFR_V1, VOTING_V1,
  CAP_TABLE_V1, CHARTER_V2_REDLINE, CHARTER_V3_CLEAN, EXECUTED_PACKAGE,
};

/* filename (as it appears on a corpus attachment / register) -> source text.
   Consumed by scripts/gen-docs.mjs to emit public/documents/<name>.docx. */
export const LUMEN_DOC_FILES = {
  "Lumen_Term_Sheet_v1.docx": TERM_SHEET_V1,
  "Lumen_Term_Sheet_signed.pdf": TERM_SHEET_SIGNED,
  "Lumen_SPA_v1.docx": SPA_V1,
  "Lumen_Charter_v1.docx": CHARTER_V1,
  "Lumen_Charter_v1_BY_markup.docx": CHARTER_V1,
  "Lumen_IRA_v1.docx": IRA_V1,
  "Lumen_ROFR_CoSale_v1.docx": ROFR_V1,
  "Lumen_Voting_Agreement_v1.docx": VOTING_V1,
  "Lumen_captable_pre-close.xlsx": CAP_TABLE_V1,
  "Lumen_Execution_Package_signed.pdf": EXECUTED_PACKAGE,
  "Lumen_Charter_v2_redline.docx": CHARTER_V2_REDLINE,
  "Lumen_Charter_v3_clean.docx": CHARTER_V3_CLEAN,
};

export { LUMEN_DOCS };

/* Served-file naming: every seed/attachment is rendered to a .docx baseline.
   docFileName maps a display filename (which may be .pdf/.xlsx) to the real
   downloadable .docx that lives under /documents/. Shared by the app UI and
   the generator so both agree on the path. */
export function docFileName(filename: string | null | undefined): string {
  const base = String(filename || "document").replace(/\.[A-Za-z0-9]+$/, "");
  return base.replace(/[^A-Za-z0-9._-]+/g, "_") + ".docx";
}
export function docHref(filename: string | null | undefined): string { return "/documents/" + docFileName(filename); }
